import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";

import { ToasterSeverities } from "@enums/toaster-severities";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import {
  AppNotification,
  NotificationStore,
  TOAST_DURATION_MS,
} from "../notification.store";

/** Icon per severity. */
export function iconFor(severity: ToasterSeverities): string {
  switch (severity) {
    case ToasterSeverities.Success:
      return "check_circle";
    case ToasterSeverities.Warn:
      return "warning";
    case ToasterSeverities.Error:
      return "error";
    default:
      return "info";
  }
}

/**
 * The stack of toasts, top right, above everything including dialogs.
 *
 * Mounted once at the application root so it works on the login page as well
 * as inside the shell. Each toast: severity icon and colour edge, the message,
 * the detail (a backend error, with a copy button for errors), a close button,
 * and - for anything but an error - a bar that drains while it is showing and
 * holds while it is hovered or focused.
 */
@Component({
  selector: "app-toast-host",
  templateUrl: "./toast-host.component.html",
  styleUrls: ["./toast-host.component.scss"],
  imports: [MatIcon, MatIconButton, MatTooltip, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastHostComponent {
  protected readonly store = inject(NotificationStore);
  private readonly translate = inject(TranslateService);
  protected readonly severities = ToasterSeverities;
  protected readonly duration = TOAST_DURATION_MS;

  /** The id whose detail was just copied, for the "Copied" tick. */
  protected readonly copiedId = signal<number | null>(null);

  protected icon(severity: ToasterSeverities): string {
    return iconFor(severity);
  }

  /**
   * Copies the whole error - what, where, when and why - in one block, ready
   * to paste into a ticket or a message to support.
   */
  protected async copy(toast: AppNotification): Promise<void> {
    const t = (key: string): string =>
      this.translate.instant(key, toast.params) as string;
    const lines = [
      toast.title ? `${t(toast.title)}: ${t(toast.message)}` : t(toast.message),
      toast.where?.length
        ? `${t("SHELL_NOTIFICATION_WHERE")}: ${toast.where.join(" › ")}`
        : "",
      `${t("SHELL_NOTIFICATION_WHEN")}: ${toast.at.toLocaleString()}`,
      toast.detail ? `${t("SHELL_NOTIFICATION_REASON")}: ${toast.detail}` : "",
    ];
    try {
      await navigator.clipboard.writeText(lines.filter(Boolean).join("\n"));
      this.copiedId.set(toast.id);
      setTimeout(() => {
        if (this.copiedId() === toast.id) {
          this.copiedId.set(null);
        }
      }, 1500);
    } catch {
      // Clipboard can be unavailable (insecure context, denied permission);
      // the detail is still selectable text.
    }
  }
}
